/**
 * The red prep timer (admin#40): one rule for every screen that shows it.
 *
 * The app owner, 2026-09-10 and again 2026-09-27: when the restaurant sets 15
 * minutes and the driver has not picked the order up within 15, the time goes
 * below zero, in red, until the driver picks it up.
 *
 * THE RULE, decided on 2026-09-27:
 * - It starts at `prep_started_at`, the moment the restaurant first saved a
 *   positive time. Not `createdAt`: the owner's words count from the time the
 *   restaurant set.
 * - The deadline is `prep_started_at + time` minutes.
 * - It runs only while the order is «قيد التحضير» or «انتهى التحضير», the two
 *   statuses in which the order waits at the restaurant (the owner, later on
 *   2026-09-27). It also stops when `picked_up_at` is set.
 * - No timer without `prep_started_at` or a positive `time`: an order the
 *   server moved to «قيد التحضير» by itself has neither.
 *
 * The restaurant tablet (talabatek-restaurant-printing#12) and the driver app
 * (talabatek-delivery-app#16) use the same base and deadline. After the flag
 * flip of api#145 the three screens agree. Until then, «قبول» moves the order
 * to `in the way` at once: the dashboard stops the timer there, and the apps
 * count on until the pickup.
 */

/**
 * The statuses in which the order waits at the restaurant for its driver:
 * «قيد التحضير» and «انتهى التحضير». After the flag flip of api#145, «قبول»
 * keeps the order in one of these two, and the pickup moves it to
 * `in the way`, so the timer runs exactly until the pickup.
 */
const WAITING_FOR_PICKUP = new Set(["preparing", "finished"]);

/**
 * The moment the restaurant's time runs out, in ms since the epoch, or null
 * when the order has no timer.
 *
 * @param {{prep_started_at?: string|null, time?: number|string|null}} order
 * @returns {number|null}
 */
export const prepDeadline = (order) => {
    const minutes = Number(order?.time);
    if (!order?.prep_started_at || !Number.isFinite(minutes) || minutes <= 0) return null;
    const start = new Date(order.prep_started_at).getTime();
    if (!Number.isFinite(start)) return null;
    return start + minutes * 60000;
};

/**
 * Whether the timer is running: a deadline exists, the order is still at the
 * restaurant, and nobody has recorded the pickup.
 *
 * @param {{status?: string, picked_up_at?: string|null}} order
 * @returns {boolean}
 */
export const isPrepTimerRunning = (order) =>
    WAITING_FOR_PICKUP.has(order?.status) && !order?.picked_up_at && prepDeadline(order) !== null;

/**
 * Whole seconds until the deadline: positive while time is left, negative once
 * the driver is late. Null when the timer is not running.
 *
 * @param {object} order
 * @param {number} nowMs
 * @returns {number|null}
 */
export const prepRemainingSeconds = (order, nowMs) => {
    if (!isPrepTimerRunning(order)) return null;
    return Math.floor((prepDeadline(order) - nowMs) / 1000);
};

/**
 * «04:12» while time is left, «-02:31» once it is over, and hours only when
 * there are any («1:05:00»).
 *
 * @param {number|null} seconds
 * @returns {string|null}
 */
export const formatCountdown = (seconds) => {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
    const total = Math.abs(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    const pad = (value) => String(value).padStart(2, "0");
    const body =
        hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${pad(minutes)}:${pad(rest)}`;
    return seconds < 0 ? `-${body}` : body;
};
